/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import { Ball } from "../items/ball/ball";
import { BallData } from "../items/ball/ball-data";
import { BallState } from "../items/ball/ball-state";
import type { FlipperMover } from "../items/flipper/flipper-mover";
import { degToRad } from "../math/float";
import { Vertex3D } from "../math/vertex3d";
import { CollisionEvent } from "../physics/collision-event";
import { DEFAULT_STEPTIME, PHYSICS_STEPTIME, STATICCNTS, STATICTIME } from "../physics/constants";
import { HitKD } from "../physics/hit-kd";
import type { HitObject } from "../physics/hit-object";
import type { HitPlane } from "../physics/hit-plane";
import { HitQuadtree } from "../physics/hit-quadtree";
import type { MoverObject } from "../physics/mover-object";
import type { Table } from "../table-model";
import type { Rng } from "./rng";

/** Time of one physics step in the unit that the collision code uses (10 ms = 1.0). */
const STEP_DT = PHYSICS_STEPTIME / DEFAULT_STEPTIME;

/** Where a new ball appears and how it moves. */
export interface IBallCreationPosition {
  getBallCreationPosition(table: Table): Vertex3D;

  getBallCreationVelocity(table: Table): Vertex3D;

  onBallCreated(physics: PlayerPhysics, ball: Ball): void;
}

/**
 * The physics loop.
 *
 * Upstream this class also runs timers, key input and the emulator, and it
 * reads the clock. Here it does one fixed step per call of `step()`. The
 * game time is `stepCount` times one step. The loop has no clock and no
 * `Math.random`. It draws random values from the injected `Rng`.
 */
export class PlayerPhysics {
  public readonly balls: Ball[] = [];
  public gravity = new Vertex3D();
  /** Game time at the start of the current step, in ms. */
  public timeMsec: number = 0;

  public recordContacts: boolean = false;
  public contacts: CollisionEvent[] = [];
  public activeBall?: Ball;
  public activeBallBC?: Ball;
  public swapBallCollisionHandling: boolean = false;
  public lastPlungerHit: number = 0;
  public ballControl = false;
  public bcTarget?: Vertex3D;
  public isPaused: boolean = false;
  public readonly rng: Rng;

  private readonly table: Table;
  private readonly movers: MoverObject[] = [];
  private readonly flipperMovers: FlipperMover[] = [];

  private readonly hitObjects: HitObject[] = [];
  private readonly hitObjectsDynamic: HitObject[] = [];
  private hitPlayfield!: HitPlane; // HitPlanes cannot be part of octree (infinite size)
  private hitTopGlass!: HitPlane;

  private hitOcTreeDynamic: HitKD = new HitKD();
  private hitOcTree: HitQuadtree = new HitQuadtree();

  private stepCount: number = 0;
  private nextBallId: number = 1;

  /**
   * Player physics are instantiated in the Player's constructor.
   * @param table The table to play.
   * @param rng The seeded random number generator.
   */
  constructor(table: Table, rng: Rng) {
    this.table = table;
    this.rng = rng;
  }

  /**
   * This is called in the player's init().
   */
  public init() {
    const data = this.table.data;
    const slope =
      data.angletiltMin + (data.angleTiltMax - data.angletiltMin) * data.globalDifficulty;

    this.gravity.x = 0;
    this.gravity.y = Math.sin(degToRad(slope)) * data.gravity;
    this.gravity.z = -Math.cos(degToRad(slope)) * data.gravity;

    // init animation timers
    for (const animatable of this.table.getAnimatables()) {
      animatable.getAnimation().init(this.timeMsec);
    }

    this.indexTableElements();
    this.initOcTree(this.table);
  }

  private indexTableElements(): void {
    // index movables
    for (const movable of this.table.getMovables()) {
      this.movers.push(movable.getMover());
    }

    // index hittables
    for (const hittable of this.table.getHittables()) {
      for (const hitObject of hittable.getHitShapes()) {
        this.hitObjects.push(hitObject);
        hitObject.calcHitBBox();
      }
    }

    this.hitObjects.push(...this.table.getHitShapes()); // these are the table's outer borders
    this.hitPlayfield = this.table.generatePlayfieldHit();
    this.hitTopGlass = this.table.generateGlassHit();

    // index flippers
    for (const flipper of Object.values(this.table.flippers)) {
      this.flipperMovers.push(flipper.getMover());
    }
  }

  private initOcTree(table: Table) {
    for (const hitObject of this.hitObjects) {
      this.hitOcTree.addElement(hitObject);
    }
    const tableBounds = table.getBoundingBox();
    this.hitOcTree.initialize(tableBounds);
    // initialize hit structure for dynamic objects
    this.hitOcTreeDynamic.fillFromVector(this.hitObjectsDynamic);
  }

  public physicsSimulateCycle(dTime: number) {
    let StaticCnts = STATICCNTS; // maximum number of static counts

    // it's okay to have this code outside of the inner loop, as the ball hitrects already include the maximum distance they can travel in that timespan
    this.hitOcTreeDynamic.update();

    while (dTime > 0) {
      let hitTime = dTime;

      // find earliest time where a flipper collides with its stop
      for (const flipperMover of this.flipperMovers) {
        const flipperHitTime = flipperMover.getHitTime();
        if (flipperHitTime > 0 && flipperHitTime < hitTime) {
          //!! >= 0.f causes infinite loop
          hitTime = flipperHitTime;
        }
      }

      this.recordContacts = true;
      CollisionEvent.release(...this.contacts);
      this.contacts.length = 0;

      for (const ball of this.balls) {
        const ballHit = ball.hit;

        if (!ball.state.isFrozen) {
          // don't play with frozen balls

          ballHit.coll.hitTime = hitTime; // search upto current hit time
          ballHit.coll.clear();

          // always check for playfield and top glass
          this.hitPlayfield.doHitTest(ball, ball.coll, this);
          this.hitTopGlass.doHitTest(ball, ball.coll, this);

          // swap order of dynamic and static obj checks randomly
          if (this.rng.coin()) {
            this.hitOcTreeDynamic.hitTestBall(ball, ball.coll, this); // dynamic objects
            this.hitOcTree.hitTestBall(ball, ball.coll, this); // find the hit objects and hit times
          } else {
            this.hitOcTree.hitTestBall(ball, ball.coll, this); // find the hit objects and hit times
            this.hitOcTreeDynamic.hitTestBall(ball, ball.coll, this); // dynamic objects
          }

          const htz = ball.coll.hitTime; // this ball's hit time

          if (htz < 0) {
            // no negative time allowed
            ball.coll.clear();
          }

          if (ball.coll.obj) {
            ///////////////////////////////////////////////////////////////////////////
            if (htz <= hitTime) {
              hitTime = htz; // record actual event time

              if (htz < STATICTIME) {
                if (--StaticCnts < 0) {
                  StaticCnts = 0; // keep from wrapping
                  hitTime = STATICTIME;
                }
              }
            }
          }
        }
      } // end loop over all balls

      this.recordContacts = false;

      // hittime now set ... or full frame if no hit
      // now update displacements to collide-contact or end of physics frame
      // !!!!! 2) move objects to hittime

      if (hitTime > STATICTIME) {
        // allow more zeros next round
        StaticCnts = STATICCNTS;
      }

      for (const mover of this.movers) {
        mover.updateDisplacements(hitTime);
      }

      // find balls that need to be collided and script'ed (generally there will be one, but more are possible)
      for (let i = 0; i < this.balls.length; i++) {
        const ball = this.balls[i]!;
        const pho = ball.coll.obj; // object that ball hit in trials

        // find balls with hit objects and minimum time
        if (pho && ball.coll.hitTime <= hitTime) {
          // now collision, contact and script reactions on active ball (object)+++++++++

          this.activeBall = ball; // The event hub reads the ball that did the collision
          pho.collide(ball.coll, this); // !!!!! 3) collision on active ball
          ball.coll.clear(); // remove trial hit object pointer

          // Collide may have changed the velocity of the ball,
          // and therefore the bounding box for the next hit cycle
          if (this.balls[i] !== ball) {
            // Ball still exists? may have been deleted from list

            // collision handler deleted the ball, back up one count
            --i;
          } else {
            ball.hit.calcHitBBox(); // do new boundings
          }
        }
      }

      /*
       * Now handle contacts.
       *
       * At this point UpdateDisplacements() was already called, so the state is different
       * from that at HitTest(). However, contacts have zero relative velocity, so
       * hopefully nothing catastrophic has happened in the meanwhile.
       *
       * Maybe a two-phase setup where we first process only contacts, then only collisions
       * could also work.
       */
      if (this.rng.coin()) {
        // swap order of contact handling randomly
        for (let i = 0; i < this.contacts.length; ++i) {
          this.contacts[i]!.obj!.contact(this.contacts[i]!, hitTime, this);
        }
      } else {
        for (let i = this.contacts.length - 1; i !== -1; --i) {
          this.contacts[i]!.obj!.contact(this.contacts[i]!, hitTime, this);
        }
      }
      CollisionEvent.release(...this.contacts);
      this.contacts.length = 0;

      dTime -= hitTime;
      this.swapBallCollisionHandling = !this.swapBallCollisionHandling; // swap order of ball-ball collisions
    }
  }

  /**
   * Runs one physics step of 1 ms.
   *
   * The caller moves the game clock. This method moves `timeMsec` to the
   * start of the next step when it ends.
   */
  public step(): void {
    this.activeBall = undefined; // No ball is the active ball for input
    this.updateVelocities();
    this.physicsSimulateCycle(STEP_DT); // main simulator call
    this.stepCount++;
    this.timeMsec = (this.stepCount * PHYSICS_STEPTIME) / 1000;
  }

  /** Number of steps that ran. */
  public getStepCount(): number {
    return this.stepCount;
  }

  public updateVelocities() {
    for (const mover of this.movers) {
      mover.updateVelocities(this); // always on integral physics frame boundary (spinner, gate, flipper, plunger, ball)
    }
  }

  public createBall(
    ballCreator: IBallCreationPosition,
    radius = 25,
    mass = 1,
    initialVelocity?: Vertex3D,
  ): Ball {
    const data = new BallData(radius, mass);
    const ballId = this.nextBallId++;
    const state = new BallState(`Ball${ballId}`, ballCreator.getBallCreationPosition(this.table));
    state.pos.z += data.radius;

    const ball = new Ball(
      ballId,
      data,
      state,
      initialVelocity ?? ballCreator.getBallCreationVelocity(this.table),
      this,
      this.table,
    );

    ballCreator.onBallCreated(this, ball);

    this.balls.push(ball);
    this.movers.push(ball.getMover()); // balls are always added separately to this list!

    this.hitObjectsDynamic.push(ball.hit);
    this.hitOcTreeDynamic.fillFromVector(this.hitObjectsDynamic);

    return ball;
  }

  public destroyBall(ball: Ball) {
    if (!ball) {
      return;
    }

    if (this.activeBallBC === ball) {
      this.activeBall = undefined;
      this.activeBallBC = undefined;
    }

    this.balls.splice(this.balls.indexOf(ball), 1);
    this.movers.splice(this.movers.indexOf(ball.getMover()), 1);
    this.hitObjectsDynamic.splice(this.hitObjectsDynamic.indexOf(ball.hit), 1);
    this.hitOcTreeDynamic.fillFromVector(this.hitObjectsDynamic);

    if (this.activeBall === ball) {
      this.activeBall = undefined;
    }
  }

  public setGravity(slopeDeg: number, strength: number): void {
    this.gravity.x = 0;
    this.gravity.y = Math.sin(degToRad(slopeDeg)) * strength;
    this.gravity.z = -Math.cos(degToRad(slopeDeg)) * strength;
  }
}
